import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Close } from "./Icons";

/** A bottom sheet, which is what a modal is on a phone.
 *
 *  It exists for the mail draft: reading an email you are about to send is a
 *  moment of its own, and having it unfold inside the page pushed everything
 *  else around and left the send button somewhere up the screen. Here it comes
 *  up over the page, the body underneath is locked, and the one button that
 *  matters sits on the bottom edge where the thumb is.
 *
 *  Escape and a tap on the dimmed area both close it, because both are what
 *  people try.
 *
 *  `full` takes the whole screen instead of rising to fit its contents. For
 *  reading a mail, fitting is right: the page stays visible behind it and the
 *  sheet is obviously a thing on top. For cropping it is wrong, because the
 *  photo is the work: sized to its contents the picture ends up small and down
 *  by the bottom edge, which is the worst place to drag a box.
 *
 *  It is drawn into the body rather than where it is written. The mail draft
 *  happens to be written at the top of its page and was fine; the crop sheet is
 *  written inside the photos card, halfway down a grid, and came up underneath
 *  the app bar, with its own title and close button hidden behind it. A modal
 *  that can be opened from anywhere cannot depend on where that anywhere is:
 *  one ancestor with a transform, a filter or a stacking context of its own is
 *  enough to trap `position: fixed`, and none of them look like they would. */
export default function Sheet({
  title,
  onClose,
  children,
  footer,
  full,
  closeLabel = "Close",
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  full?: boolean;
  /** What a screen reader calls the close button. The customer's pages pass
   *  their own words for it; the studio, in English, takes the default. */
  closeLabel?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    // Without this the page behind scrolls when the sheet's own content
    // reaches its end, which on a phone reads as the sheet falling apart.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="sheet-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
    >
      <div className={`sheet${full ? " sheet-full" : ""}${footer ? " sheet-has-foot" : ""}`}>
        {/* The handle says "this rises to fit and can be pulled". A full screen
            does neither, so it would be a lie about the gesture. */}
        {!full && <div className="sheet-grab" />}
        <div className="sheet-head">
          <h2 className="grow" style={{ margin: 0, fontSize: 19 }}>
            {title}
          </h2>
          <button className="iconbtn iconbtn-right" aria-label={closeLabel} onClick={onClose}>
            <Close />
          </button>
        </div>
        <div className="sheet-body selectable">{children}</div>
        {footer && <div className="sheet-foot">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
